// src/services/currencyService.js
import {
  doc,
  getDoc
} from 'firebase/firestore';
import { db } from '../firebase';

class CurrencyService {
  constructor() {
    this.baseCurrency = 'USD';
    this.exchangeRatesCache = null;
    this.cacheExpiry = null;
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes
  }

  // Get all exchange rates
  async getExchangeRates() {
    try {
      // Check cache first
      if (this.exchangeRatesCache && this.cacheExpiry && Date.now() < this.cacheExpiry) {
        return this.exchangeRatesCache;
      }

      // Fetch from Firestore - exchange rates are stored in settings/exchangeRates document
      const ratesDoc = await getDoc(doc(db, 'settings', 'exchangeRates'));

      if (ratesDoc.exists()) {
        const ratesData = ratesDoc.data();
        
        // Cache the rates
        this.exchangeRatesCache = ratesData.rates || {};
        this.cacheExpiry = Date.now() + this.cacheTimeout;
        
        return this.exchangeRatesCache;
      }

      // Return empty rates if none found
      return {};
    } catch (error) {
      console.error('Error fetching exchange rates:', error);
      return {};
    }
  }

  // Convert USD amount to target currency
  async convertFromUSD(usdAmount, targetCurrency) {
    if (targetCurrency === 'USD' || !targetCurrency) {
      return {
        amount: usdAmount,
        currency: 'USD',
        rate: 1
      };
    }

    try {
      const rates = await this.getExchangeRates();
      const rate = rates[targetCurrency];

      if (!rate) {
        const error = `Exchange rate not found for ${targetCurrency}`;
        console.error(error);
        throw new Error(error);
      }

      return {
        amount: usdAmount * rate,
        currency: targetCurrency,
        rate: rate,
        originalAmount: usdAmount,
        originalCurrency: 'USD'
      };
    } catch (error) {
      console.error('Error converting from USD:', error);
      throw error;
    }
  }

  // Convert from any currency to USD
  async convertToUSD(amount, fromCurrency) {
    if (fromCurrency === 'USD' || !fromCurrency) {
      return {
        amount: amount,
        currency: 'USD',
        rate: 1
      };
    }

    try {
      const rates = await this.getExchangeRates();
      const rate = rates[fromCurrency];

      if (!rate) {
        const error = `Exchange rate not found for ${fromCurrency}`;
        console.error(error);
        throw new Error(error);
      }

      return {
        amount: amount / rate,
        currency: 'USD',
        rate: 1 / rate,
        originalAmount: amount,
        originalCurrency: fromCurrency
      };
    } catch (error) {
      console.error('Error converting to USD:', error);
      throw error;
    }
  }

  // Format currency amount with symbol and proper decimals
  formatCurrency(amount, currency, showSymbol = true) {
    const currencySymbols = {
      USD: '$',
      EUR: '€',
      GBP: '£',
      JPY: '¥',
      CAD: 'C$',
      AUD: 'A$',
      CHF: 'CHF',
      CNY: '¥',
      INR: '₹',
      ALL: 'L'
    };

    const decimals = currency === 'JPY' ? 0 : 2;
    const formattedAmount = amount.toFixed(decimals);
    
    if (!showSymbol) {
      return formattedAmount;
    }

    const symbol = currencySymbols[currency] || currency;
    return `${symbol}${formattedAmount}`;
  }

  // Get display text for allowance with currency conversion
  async getAllowanceDisplayText(usdAmount, displayCurrency = 'USD', showBoth = true) {
    if (displayCurrency === 'USD' || !showBoth) {
      const converted = await this.convertFromUSD(usdAmount, displayCurrency);
      return this.formatCurrency(converted.amount, converted.currency);
    }

    // Show both USD and converted amount
    const converted = await this.convertFromUSD(usdAmount, displayCurrency);
    const usdText = this.formatCurrency(usdAmount, 'USD');
    const convertedText = this.formatCurrency(converted.amount, converted.currency);
    
    return `${usdText} (${convertedText})`;
  }

  // Calculate surcharge with currency support
  async calculateSurcharge(roomPriceUSD, dailyAllowanceUSD, userBalanceUSD = 0) {
    const totalCoverage = dailyAllowanceUSD + userBalanceUSD;
    const surchargeUSD = Math.max(0, roomPriceUSD - totalCoverage);
    const passCoveredUSD = Math.min(roomPriceUSD, totalCoverage);

    return {
      surcharge: surchargeUSD,
      passCovers: passCoveredUSD,
      userPays: surchargeUSD,
      currency: 'USD',
      breakdown: {
        roomPrice: roomPriceUSD,
        dailyAllowance: dailyAllowanceUSD,
        userBalance: userBalanceUSD,
        totalCoverage: totalCoverage
      }
    };
  }

  // Get surcharge display text with currency conversion
  async getSurchargeDisplayText(surchargeUSD, displayCurrency = 'USD') {
    if (surchargeUSD === 0) {
      return 'No surcharge';
    }

    const converted = await this.convertFromUSD(surchargeUSD, displayCurrency);
    return `Surcharge: ${this.formatCurrency(converted.amount, converted.currency)}`;
  }

  // Get available currencies from exchange rates
  async getAvailableCurrencies() {
    try {
      const rates = await this.getExchangeRates();
      const currencies = ['USD', ...Object.keys(rates)].filter(Boolean);
      
      // Sort by common currencies first
      const commonCurrencies = ['USD', 'EUR', 'GBP', 'CAD', 'AUD'];
      const sorted = [
        ...commonCurrencies.filter(c => currencies.includes(c)),
        ...currencies.filter(c => !commonCurrencies.includes(c)).sort()
      ];

      return [...new Set(sorted)]; // Remove duplicates
    } catch (error) {
      console.error('Error getting available currencies:', error);
      return ['USD'];
    }
  }

  // Clear cache (useful for forcing refresh)
  clearCache() {
    this.exchangeRatesCache = null;
    this.cacheExpiry = null;
  }
}

const currencyService = new CurrencyService();
export default currencyService;